import { ContentMDX } from './content-mdx';
import { ContentStyleProse, type ContentStyleProseProps } from './content-style-prose';

export interface ContentProps extends Omit<ContentStyleProseProps, 'children'> {
  /**
   * The style format of the content.
   * @default 'default'
   */
  styling?: 'prose' | 'default';
  /**
   * The format of the content.
   * @default 'html'
   */
  format?: 'mdx' | 'html';
  /**
   * The maximum width of the content.
   */
  className?: string;
  /**
   * The content to display (raw HTML converted from Markdown).
   */
  content: string;
}

/**=
 * Viewer for collection content.
 *
 * @param props A collection content.
 * @returns The content rendered in the specified format.
 */
export function Content({
  content,
  styling = 'default',
  format = 'html',
  maxWidth,
  className,
}: ContentProps) {
  if (styling === 'prose') {
    return (
      <ContentStyleProse maxWidth={maxWidth} className={className}>
        <ContentRenderer content={content} format={format} />
      </ContentStyleProse>
    );
  }

  return <ContentRenderer content={content} format={format} className={className} />;
}

/*
|------------------
| Internal
|------------------
*/

function ContentRenderer(props: Pick<ContentProps, 'content' | 'format' | 'className'>) {
  if (props.format === 'mdx') {
    return <ContentMDX code={props.content} />;
  }

  return (
    <span
      className={props.className}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: We trust the content
      dangerouslySetInnerHTML={{
        __html: props.content,
      }}
    />
  );
}
